import {
  Dispatch,
  ReactElement,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Card,
  Column,
  Empty,
  Flex,
  Heading,
  Icon,
  Tree,
  Row,
  Spacer,
  Spinner,
} from '@oliasoft-open-source/react-ui-library';
import { TbFolder, TbFolderOpen } from 'react-icons/tb';
import translations from 'client/internationalization/translation-map.json';
import { getIPMFilesLoadingSelector, getIPMFilesSelector } from 'client/store/entities/ipm-files/selectors';
import { IIPMFile, IPMFileType } from 'common/interfaces/settings';
import { IObject } from 'common/interfaces/hierarchy-entities';

type IPMFilesExplorerProps = {
  selectedData: IIPMFile | IObject | null;
  setSelectedData: Dispatch<SetStateAction<IIPMFile | IObject | null>>;
  setDataViewerVisible: (bool: boolean) => void;
};

type IPMFileListItemProps = {
  active: boolean;
  restricted: boolean;
  entityType: IPMFileType;
  id: string;
  parent: string | number;
  name: ReactElement;
  actions: {
    subActions: {
      label: string;
      onClick: () => void;
    }[]
  }[];
  onClick: () => void;
};

export const IPMFilesExplorer = ({
  selectedData,
  setSelectedData,
  setDataViewerVisible,
}: IPMFilesExplorerProps) => {
  const { t } = useTranslation();
  const ipmFiles = useSelector(getIPMFilesSelector);
  const isLoading = useSelector(getIPMFilesLoadingSelector);

  const viewData = (data: IIPMFile) => {
    setSelectedData(data);
    setDataViewerVisible(true);
  };

  const getChildHierarchyItems = () => {
    const items = ipmFiles.map((object) => {
      const treeItem: IPMFileListItemProps = {
        name: (
          <Flex alignItems="center" gap="var(--padding-xs)">
            {object.type !== IPMFileType.Folder && <Icon icon='file' size={15} />}
            {object.name}
          </Flex>
        ),
        id: object.id,
        parent: object.parentId || 0,
        onClick: () => {
          setSelectedData(object);
          viewData(object);
        },
        // @ts-ignore
        active: selectedData?.id === object.id,
        actions: [],
        restricted: false,
        entityType: object.type,
      };

      return treeItem;
    });

    return items;
  };

  const childHierarchyItems = getChildHierarchyItems();
  const getItemHasChild = ({ data }: { data?: IPMFileListItemProps }) => {
    return data?.entityType === IPMFileType.Folder;
  }

  const itemChildList = (
    <div>
      <Spacer />
      {childHierarchyItems.length > 0 ? (
        <Card padding={false}>
          <Tree
            list={{
              items: childHierarchyItems,
            }}
            itemHasChild={getItemHasChild}
            icons={{
              expand: <TbFolder />,
              collapse: <TbFolderOpen />,
            }}
          />
        </Card>
      ) : (
        <Empty
          height="100%"
          width="100%"
          text={t(translations.viewer_noData)}
        />
      )}
    </div>
  );

  const dataList = (
    <>
      <Row spacing={0}>
        <Column>
          <Heading>
            {`${t(translations.files)} (${ipmFiles?.length})`}
            <Spacer width="var(--padding-xxs)" />
            {isLoading && (
              <Flex justifyContent="center">
                <Spinner dark tiny />
              </Flex>
            )}
          </Heading>
        </Column>
      </Row>
      <Spacer />
      <Row>
        <Column>
          {itemChildList}
          <Spacer />
        </Column>
      </Row>
    </>
  );

  return <Column>{dataList}</Column>;
};
